import { FormControl, TextFieldProps } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { DateValidationError } from '@mui/x-date-pickers/models';
import moment from 'moment';
import React from 'react';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import { DATE_FORMAT, DEFAULT_DATE_FORMAT } from 'src/constants';
import { FormBodyAPI } from 'src/constants/fieldsTypes';
import { BaseTextField } from 'src/legacy/components/TextField';

// Kept for consumers (e.g. useShareContract) that still pass it as a custom slot.
const dateErrorMessageMap: Record<string, string> = {
  'Invalid Date Format': 'Invalid date format',
};

const SUPPORTED_DATE_FORMATS = [DATE_FORMAT, DEFAULT_DATE_FORMAT];

export const parseDateValue = (value: string | null | undefined) => {
  if (!value) return null;

  const parsedDate = moment(value, SUPPORTED_DATE_FORMATS, true);
  return parsedDate.isValid() ? parsedDate : null;
};

export const DateTextField: React.FC<TextFieldProps> = (props) => {
  const { error, helperText, value, ...rest } = props;
  const errorMessage = () => {
    if (!error) return '';
    if (!value) return 'Date is required';

    if (typeof helperText === 'string') {
      return dateErrorMessageMap[helperText] || helperText;
    }
    return helperText;
  };

  return (
    <BaseTextField
      sizeVariant="medium"
      value={value}
      error={error}
      helperText={errorMessage()}
      {...rest}
    />
  );
};

const getDateErrorMessage = (reason: DateValidationError): string => {
  switch (reason) {
    case 'invalidDate':
      return 'Invalid date';
    case 'minDate':
      return 'Date should not be before minimal date';
    case 'maxDate':
      return 'Date should not be after maximal date';
    case 'disableFuture':
      return 'Future dates are not allowed';
    case 'disablePast':
      return 'Past dates are not allowed';
    default:
      return '';
  }
};

export const useAddDateForm = () => {
  const validationScheme = Yup.object().shape({
    value: Yup.date().typeError('Invalid date').required('Date is required'),
  });

  const getSubmitValues = async () => ({
    key: v4(),
  });

  const FormRenderer: React.FC<FormBodyAPI> = ({
    setFieldValue,
    values,
    errors,
    touched,
    handleBlur,
  }) => {
    const [dateError, setDateError] = React.useState<DateValidationError>(null);

    const formikError =
      touched?.value && typeof errors?.value === 'string' ? errors.value : '';
    const pickerErrorMessage = getDateErrorMessage(dateError);
    const hasError = Boolean(pickerErrorMessage) || Boolean(formikError);
    const helperText = pickerErrorMessage || formikError;

    return (
      <div>
        <FormControl fullWidth>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              name="value"
              format={DATE_FORMAT}
              value={parseDateValue(values.value)}
              onChange={(val) => {
                setFieldValue(
                  'value',
                  val && val.isValid() ? val.format(DATE_FORMAT) : null,
                );
              }}
              onError={(reason) => setDateError(reason)}
              enableAccessibleFieldDOMStructure={false}
              slots={{ textField: DateTextField }}
              slotProps={{
                textField: {
                  name: 'value',
                  variant: 'outlined',
                  margin: 'none',
                  onBlur: handleBlur,
                  error: hasError,
                  helperText,
                },
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </div>
    );
  };

  return { validationScheme, getSubmitValues, FormRenderer };
};
